/**
 * DeraR2Geocoder - Geocodificador L4 usando datos DERA desde Cloudflare R2
 * 
 * Fuente: DERA (Datos Espaciales de Referencia de Andalucía) del IECA
 * Endpoint: https://geodata.pteltool.es/dera/dera-infraestructuras.json
 * 
 * Tipologías: SANITARIO, EDUCATIVO, SEGURIDAD, MUNICIPAL, ENERGIA, EMERGENCIA
 * Precisión: ±5-15m (coordenadas oficiales UTM30 ETRS89)
 * 
 * @module services/geocoding/r2/DeraR2Geocoder
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type { GeocodingResult } from '../../../types/infrastructure';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 *
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 * Se preserva el nombre interno `R2_BASE_URL` para minimizar churn en
 * la URL derivada `DERA_ENDPOINT` declarada justo debajo.
 */
const R2_BASE_URL = WORKER_BASE;
const DERA_ENDPOINT = `${R2_BASE_URL}/dera/dera-infraestructuras.json`;
const FETCH_TIMEOUT_MS = 10000;
const _MIN_FUZZY_SCORE = 0.5; // Score mínimo para considerar match válido

// =============================================================================
// TIPOS
// =============================================================================

/** Feature DERA individual del JSON R2 */
export interface DeraFeature {
  id: string;
  tipologia: string;
  nombre: string;
  subtipo: string;
  direccion: string;
  localidad: string;
  codMun: string;
  municipio: string;
  provincia: string;
  x: number;
  y: number;
}

/** Estructura del JSON DERA completo en R2 */
interface DeraData {
  type: string;
  metadata: {
    totalFeatures: number;
    transformedAt: string;
    sources: Array<{ file: string; tipologia: string; count: number }>;
  };
  features: DeraFeature[];
}

/** Opciones para geocodificación DERA */
export interface DeraGeocodingOptions {
  /** Código INE del municipio (5 dígitos) para filtrar resultados */
  codMun?: string;
  /** Tipología para filtrar (SANITARIO, EDUCATIVO, etc.) */
  tipologia?: string;
  /** Límite de resultados a evaluar */
  maxCandidates?: number;
}

// =============================================================================
// CACHÉ SINGLETON
// =============================================================================

/** Caché en memoria de datos DERA (singleton) */
let deraCache: DeraData | null = null;
let deraCacheTimestamp: number | null = null;

/** Instancia uFuzzy configurada para español */
const fuzzyMatcher = new uFuzzy({
  intraMode: 1,        // Permitir caracteres entre matches
  intraIns: 2,         // Inserciones permitidas
  intraSub: 1,         // Sustituciones permitidas
  intraTrn: 1,         // Transposiciones permitidas
  interIns: 3,         // Inserciones entre palabras
});

// =============================================================================
// FUNCIONES PRINCIPALES
// =============================================================================

/**
 * Carga datos DERA desde R2 (con caché singleton)
 * 
 * @returns Promesa con datos DERA o null si error
 */
export async function loadDeraData(): Promise<DeraData | null> {
  // Retornar caché si existe
  if (deraCache !== null) {
    return deraCache;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const response = await fetch(DERA_ENDPOINT, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[DeraR2] Error HTTP ${response.status}: ${response.statusText}`);
      return null;
    }

    const data: DeraData = await response.json();
    
    // Validar estructura mínima
    if (!data.features || !Array.isArray(data.features) || !data.metadata) {
      console.error('[DeraR2] Estructura JSON inválida: falta array features o metadata');
      return null;
    }

    // Guardar en caché
    deraCache = data;
    deraCacheTimestamp = Date.now();
    
    console.log(`[DeraR2] Cargadas ${data.metadata.totalFeatures} infraestructuras DERA desde R2`);
    return data;

  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error(`[DeraR2] Timeout después de ${FETCH_TIMEOUT_MS}ms`);
    } else {
      console.error('[DeraR2] Error cargando datos:', error);
    }
    return null;
  }
}

/**
 * Geocodifica una infraestructura usando datos DERA R2
 * 
 * @param query - Nombre de la infraestructura a buscar
 * @param options - Opciones de filtrado (codMun, tipologia)
 * @returns Resultado de geocodificación o null si no hay match
 */
export async function geocodeWithDeraR2(
  query: string,
  options: DeraGeocodingOptions = {}
): Promise<GeocodingResult | null> {
  const { codMun, tipologia, maxCandidates = 100 } = options;

  // Cargar datos si no están en caché
  const data = await loadDeraData();
  if (!data) {
    return null;
  }

  // Filtrar por municipio y tipología si se especifican
  let candidates = data.features;
  
  if (codMun) {
    candidates = candidates.filter(f => f.codMun === codMun);
  }
  
  if (tipologia) {
    candidates = candidates.filter(f => 
      f.tipologia.toUpperCase() === tipologia.toUpperCase()
    );
  }

  // Si no hay candidatos después del filtro, retornar null
  if (candidates.length === 0) {
    return null;
  }

  // Limitar candidatos para búsqueda
  const searchCandidates = candidates.slice(0, maxCandidates);

  // Preparar haystack para uFuzzy (nombres de infraestructuras)
  const haystack = searchCandidates.map(f => f.nombre);

  // Buscar con uFuzzy
  const normalizedQuery = query.trim().toUpperCase();
  const [idxs, info, order] = fuzzyMatcher.search(haystack, normalizedQuery);

  // Si no hay resultados, retornar null
  if (!idxs || idxs.length === 0 || !order || order.length === 0) {
    return null;
  }

  // Tomar el mejor resultado
  const bestIdx = order[0];
  const matchedFeatureIdx = idxs[bestIdx];
  const matchedFeature = searchCandidates[matchedFeatureIdx];

  // Calcular score de confianza (0-100) usando info de uFuzzy
  const queryLen = normalizedQuery.length;
  const matchedName = haystack[matchedFeatureIdx];
  
  // info.chars[bestIdx] = caracteres de la query que matchearon
  // Esto es la métrica correcta: qué % de la query encontramos
  const charsMatched = info?.chars?.[bestIdx] ?? 0;
  const matchRatio = queryLen > 0 ? charsMatched / queryLen : 0;
  
  // Bonus si es match exacto (ignorando case)
  const isExactMatch = matchedName.toUpperCase() === normalizedQuery;
  
  // Score final:
  // - Match exacto: 95%
  // - Match parcial: 60 base + hasta 35 por calidad (matchRatio)
  // Ejemplo: "Hospital" (8 chars) matchea 8 chars → 60 + 35 = 95%
  const confidence = isExactMatch 
    ? 95 
    : Math.round(60 + (matchRatio * 35));

  // Si el score es muy bajo, no retornar resultado
  if (confidence < 50) {
    return null;
  }

  // Construir resultado
  const result: GeocodingResult = {
    x: matchedFeature.x,
    y: matchedFeature.y,
    confidence,
    source: 'dera_r2',
    sourceLayer: 'L4',
    originalQuery: query,
    matchedName: matchedFeature.nombre,
    matchType: isExactMatch ? 'exact' : 'fuzzy',
    fuzzyScore: matchRatio,
    municipality: matchedFeature.municipio,
    muniCode: matchedFeature.codMun,
    province: matchedFeature.provincia,
    metadata: {
      id: matchedFeature.id,
      tipologia: matchedFeature.tipologia,
      subtipo: matchedFeature.subtipo,
      direccion: matchedFeature.direccion,
      localidad: matchedFeature.localidad,
    },
  };

  return result;
}

/**
 * Limpia la caché DERA (útil para tests y recarga forzada)
 */
export function clearDeraCache(): void {
  deraCache = null;
  deraCacheTimestamp = null;
}

/**
 * Obtiene estadísticas de la caché DERA
 */
export function getDeraCacheStats(): {
  loaded: boolean;
  count: number | null;
  loadedAt: Date | null;
} {
  return {
    loaded: deraCache !== null,
    count: deraCache?.metadata?.totalFeatures ?? null,
    loadedAt: deraCacheTimestamp ? new Date(deraCacheTimestamp) : null,
  };
}
